var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useI18n } from '@cta-portal/webapp-common/lib/hooks/translations/use-i18n';
import React, { useContext } from 'react';
import styled from 'styled-components';
import { RoutePath, generateRoutePath } from '../../../app/router/config';
import addIcon from '../../../assets/images/icons/common/icon-add-purple.png';
import extendIcon from '../../../assets/images/icons/common/icon-extend-purple.png';
import dateIcon from '../../../assets/images/icons/common/icon-lockup-period-gold.png';
import stageIcon from '../../../assets/images/icons/common/icon-rank-gold.png';
import vpIcon from '../../../assets/images/icons/common/icon-victory-point-gold.png';
import { CurrencyContext } from '../../../context/currency';
import { CurrencyTokenSymbol } from '../../../data/currency-tokens/currency-tokens';
import { appEnv } from '../../../env';
import { useMyStakings } from '../../../hooks/cta-token/use-my-stakings';
import { DateSuffix, formatDate } from '../../../utils/date';
import Button from '../../common/button';
import Table, { CellAlign, CellType } from '../../common/table';
export default function MyStakings() {
    var _a = useI18n(), t = _a.t, i18n = _a.i18n;
    var ctaTokenUsdRate = useContext(CurrencyContext).ctaTokenUsdRate;
    var stakingEntries = useMyStakings().stakingEntries;
    var labels = [
        {
            key: 'dateStart',
            label: t('common.date-start'),
            align: CellAlign.LEFT,
            size: 1,
            icon: dateIcon,
            hiddenMobile: true,
            showExtendMobile: true,
        },
        {
            key: 'expireAt',
            label: t('common.expire-at'),
            align: CellAlign.LEFT,
            size: 1,
            icon: dateIcon,
            hiddenMobile: true,
            showExtendMobile: true,
        },
        {
            key: 'mintPass',
            label: t('common.mint-pass'),
            align: CellAlign.CENTER,
            size: 0.8,
        },
        {
            key: 'stage',
            label: t('common.stage'),
            align: CellAlign.CENTER,
            size: 0.5,
            icon: stageIcon,
            hiddenMobile: true,
            showExtendMobile: true,
        },
        {
            key: 'vipPoints',
            label: t('common.vip-pts'),
            align: CellAlign.CENTER,
            size: 0.5,
            icon: vpIcon,
            hiddenMobile: true,
            showExtendMobile: true,
        },
        {
            key: 'tokens',
            label: t('common.tokens'),
            align: CellAlign.RIGHT,
            size: 1,
        },
        {
            key: 'action',
            label: t('common.actions'),
            align: CellAlign.CENTER,
            size: 1,
            actionCell: true,
        },
    ];
    var rows = stakingEntries
        ? stakingEntries.map(function (entry, index) {
            var mintPassImgUrl = entry.mintPass
                ? "".concat(appEnv.marketplaceImagesUrl.mintPass).concat(entry.mintPass.type.replaceAll('_', '-').toLowerCase(), "-0.png")
                : undefined;
            var amount = Number.parseFloat(entry.amountTotalDeci10) / 10000000000;
            return {
                id: index.toString(),
                data: {
                    dateStart: {
                        type: CellType.TEXT,
                        value: formatDate(entry.startAt, i18n.language, DateSuffix.MINUTES),
                    },
                    expireAt: {
                        type: CellType.TEXT,
                        value: formatDate(entry.expireAt, i18n.language, DateSuffix.MINUTES),
                    },
                    mintPass: {
                        type: CellType.IMAGE,
                        value: mintPassImgUrl,
                    },
                    stage: {
                        type: CellType.NUMBER,
                        value: entry.mintPass
                            ? "".concat(entry.stages.length, "/").concat(entry.mintPass.maxStage)
                            : "".concat(entry.stages.length, "/").concat(entry.stages.length),
                    },
                    vipPoints: {
                        type: CellType.NUMBER,
                        value: "".concat(entry.totalLoyaltyPoints, " VP"),
                    },
                    tokens: {
                        type: CellType.PRICE,
                        value: {
                            tokenSymbol: CurrencyTokenSymbol.CTA,
                            value: amount.toLocaleString('en-US', {
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 4,
                            }),
                            usdValue: (amount * ctaTokenUsdRate).toLocaleString('en-US', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                            }),
                        },
                    },
                },
            };
        })
        : [];
    var actions = [
        {
            icon: addIcon,
            link: {
                routePath: RoutePath.STAKING_FILL,
                generateParams: function (_id) { return ({}); },
                generateState: function (id) {
                    var index = Number.parseInt(id);
                    if (isNaN(index) || !stakingEntries || !stakingEntries[index])
                        return undefined;
                    return {
                        mintPass: stakingEntries[index].mintPass,
                        weekCountDurationRemaining: stakingEntries[index].weekCountDurationRemaining,
                    };
                },
            },
            generateInvisibility: function (id) {
                var index = Number.parseInt(id);
                if (isNaN(index) || !stakingEntries || !stakingEntries[index])
                    return true;
                var entry = stakingEntries[index];
                return entry.mintPass ? entry.stages.length === entry.mintPass.maxStage : true;
            },
        },
        {
            icon: extendIcon,
            link: {
                routePath: RoutePath.STAKING_EXTEND,
                generateParams: function (_id) { return ({}); },
                generateState: function (id) {
                    var index = Number.parseInt(id);
                    if (isNaN(index) || !stakingEntries || !stakingEntries[index])
                        return undefined;
                    var entry = stakingEntries[index];
                    return {
                        mintPass: entry.mintPass,
                        ctaTokenValue: Number.parseFloat(entry.amountTotalDeci10) / 10000000000,
                        weekCountDurationRemaining: entry.weekCountDurationRemaining,
                        staking: !entry.mintPass ? entry.stages[0] : undefined,
                    };
                },
            },
            generateInvisibility: function (id) {
                var index = Number.parseInt(id);
                if (isNaN(index) || !stakingEntries || !stakingEntries[index])
                    return true;
                var entry = stakingEntries[index];
                return entry.weekCountDurationRemaining === 36 || (entry.mintPass === undefined && entry.stages.length > 0);
            },
        },
    ];
    return (_jsxs(Container, { children: [_jsx(Button, { icon: addIcon, link: generateRoutePath(RoutePath.STAKING_CREATE, {}), children: t('staking.new-stake') }), _jsx(Table, { actions: actions, labels: labels, rows: rows })] }));
}
var Container = styled.div(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  align-items: center;\n  display: flex;\n  flex-direction: column;\n  gap: 24px;\n  justify-content: flex-start;\n  padding: 40px 64px;\n  width: 100%;\n\n  @media screen and (max-width: 824px) {\n    padding: 40px 16px;\n  }\n"], ["\n  align-items: center;\n  display: flex;\n  flex-direction: column;\n  gap: 24px;\n  justify-content: flex-start;\n  padding: 40px 64px;\n  width: 100%;\n\n  @media screen and (max-width: 824px) {\n    padding: 40px 16px;\n  }\n"])));
var templateObject_1;
